import { Modal } from '@/components/ui/modal';
import { useServices } from '@/app/providers/ServiceContext';
import { useToast } from '@/app/providers/ToastContext';
import { useConfirmation } from '@/app/providers/ConfirmationContext';
import type { AccessDoor, CameraRecord } from '@yawi/types';
import { Camera, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

interface EditDoorCameraModalProps {
  door: AccessDoor | null;
  tenantId: string;
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
}

export function EditDoorCameraModal({ door, tenantId, isOpen, onClose, onSaved }: EditDoorCameraModalProps) {
  const { accessDoorRepository, cameraRepository } = useServices();
  const { showToast } = useToast();
  const { confirm } = useConfirmation();

  const [cameras, setCameras] = useState<CameraRecord[]>([]);
  const [camerasLoading, setCamerasLoading] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const loadCameras = useCallback(async () => {
    setCamerasLoading(true);
    try {
      const res = await cameraRepository.listCameras(1, 100, tenantId);
      setCameras(res.data);
    } catch (e) {
      showToast('error', e instanceof Error ? e.message : 'Error al cargar cámaras');
    } finally {
      setCamerasLoading(false);
    }
  }, [cameraRepository, showToast]);

  useEffect(() => {
    if (isOpen && door) {
      setSelected(door.primaryCameraIndexCode);
      loadCameras();
    }
  }, [isOpen, door, loadCameras]);

  const handleSave = async () => {
    if (!door) return;
    const cam = cameras.find((c) => c.indexCode === selected);
    if (cam?.status === 'offline') {
      const confirmed = await confirm({
        title: 'Cámara offline',
        message: `La cámara "${cam.name}" está offline. Se asignará igual, pero no generará snapshots hasta que vuelva.`,
        confirmText: 'Continuar',
        cancelText: 'Cancelar',
        variant: 'warning',
      });
      if (!confirmed) return;
    }
    setSaving(true);
    try {
      await accessDoorRepository.updateDoorCamera(door.id, { primaryCameraIndexCode: selected }, tenantId);
      showToast('success', 'Cámara actualizada');
      onSaved();
      onClose();
    } catch (e) {
      showToast('error', e instanceof Error ? e.message : 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    if (!door) return;
    const confirmed = await confirm({
      title: '¿Quitar asignación de cámara?',
      message: 'El próximo auto-mapeo podrá reasignarle una cámara a esta puerta.',
      confirmText: 'Quitar',
      cancelText: 'Cancelar',
      variant: 'warning',
    });
    if (!confirmed) return;
    setSaving(true);
    try {
      await accessDoorRepository.updateDoorCamera(door.id, { primaryCameraIndexCode: null }, tenantId);
      showToast('success', 'Asignación de cámara eliminada');
      onSaved();
      onClose();
    } catch (e) {
      showToast('error', e instanceof Error ? e.message : 'Error al limpiar asignación');
    } finally {
      setSaving(false);
    }
  };

  if (!door) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="max-w-lg w-full">
      <div className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <Camera className="w-5 h-5 text-indigo-500" />
          <h2 className="text-lg font-semibold text-theme">Editar cámara — {door.name}</h2>
        </div>

        {camerasLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 text-indigo-500 animate-spin" />
          </div>
        ) : (
          <div className="mb-5">
            <label className="block text-sm font-medium text-theme mb-2">Cámara asignada</label>
            <select
              value={selected ?? ''}
              onChange={(e) => setSelected(e.target.value || null)}
              className="w-full border border-border rounded-lg px-3 py-2 text-sm text-theme bg-surface focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">— Sin cámara —</option>
              {cameras.map((cam) => (
                <option key={cam.indexCode} value={cam.indexCode}>
                  {cam.name}{cam.status === 'offline' ? ' (offline)' : ''}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="flex justify-between items-center">
          <button
            type="button"
            onClick={handleClear}
            disabled={saving || !door.primaryCameraIndexCode}
            className="text-sm text-red-500 hover:text-red-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Limpiar asignación
          </button>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 text-sm text-theme-secondary hover:text-theme transition-colors disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || camerasLoading}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Guardar
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
